/**
 * The approved wind/hail deductible exception on an FO-6 structure or a Coverage F item. #36996
 *
 * GO rated CFM structures and Coverage F articles at a 1% wind/hail deductible. The dollar amount it
 * applied (and printed on the dec) was `max(1% × limit, all-peril deductible, $2,500)`. EmberTech's
 * rule (staff-fe `getWindHailOptions`) doesn't allow a percentage whose amount is below the
 * all-peril deductible or the floor, so the importer used to settle GO's 1% to that dollar amount.
 * ClarionDoor then rated the dollar amount as the nearest filed percentage (1.5% or 2%), which
 * under-charged the imported term.
 *
 * The CFM GO importer now stores GO's percentage and records it as approved, with the same
 * reason / by / at audit trail as the #35617 coverage-limit override:
 *   windHailDeductibleOverride        the approved value (e.g. 0.01)
 *   windHailDeductibleOverrideReason  why
 *   windHailDeductibleOverrideBy      who ('guidewire-import')
 *   windHailDeductibleOverrideAt      when
 *
 * The approval is the VALUE, not a flag. It holds only while `deductibleWindHail` still equals it.
 * Once staff pick another option, the ordinary rule applies to the new value again, with nothing to
 * clear. Renewals clone the coverages, so the approval carries forward unchanged.
 */

import { resolveDeductibleAmount } from './deductible-amount';

/** GO's wind/hail floor for CFM: the amount it applied when 1% and the all-peril deductible were both lower. */
export const GRANDFATHERED_WIND_HAIL_DEDUCTIBLE_FLOOR = 2500;

/** A structure's coverages or a Coverage F item: whatever carries `deductibleWindHail` and its approval. */
export interface WindHailDeductibleHolder {
  deductibleWindHail?: number | null;
  windHailDeductibleOverride?: number | null;
}

/** True while the stored wind/hail deductible is the approved exception. */
export function isWindHailDeductibleOverridden(holder: WindHailDeductibleHolder | null | undefined): boolean {
  const approved = holder?.windHailDeductibleOverride;
  const stored = holder?.deductibleWindHail;
  return approved != null && stored != null && Number(stored) === Number(approved);
}

/**
 * The wind/hail deductible in dollars, as printed on the dec.
 *
 * An approved percentage applies as GO applied it: never below the all-peril deductible or GO's
 * $2,500 floor. FO0115504-04's $375,000 barn at 1% with a $5,000 all-peril deductible prints
 * $5,000, as on GO's dec, not $3,750. Every other value resolves exactly as `resolveDeductibleAmount`.
 */
export function resolveWindHailDeductibleAmount({
  deductibleWindHail,
  windHailDeductibleOverride,
  coverageLimit,
  deductibleAllOther
}: WindHailDeductibleHolder & {
  coverageLimit: number | null | undefined;
  deductibleAllOther?: number | null;
}): number {
  const amount = resolveDeductibleAmount(deductibleWindHail, coverageLimit || 0);
  if (!isWindHailDeductibleOverridden({ deductibleWindHail, windHailDeductibleOverride })) {
    return amount;
  }
  return Math.max(
    amount,
    resolveDeductibleAmount(deductibleAllOther, coverageLimit || 0),
    GRANDFATHERED_WIND_HAIL_DEDUCTIBLE_FLOOR
  );
}
